import Phaser from "phaser";
import { GameConstants } from "../GameConstants";
import { GameManager } from "../GameManager";
import { fitLayout } from "../ui/Layout";

export class PreloadScene extends Phaser.Scene {
    private progressBar!: Phaser.GameObjects.Graphics;
    private scaledItemsContainer!: Phaser.GameObjects.Container;
    private background!: Phaser.GameObjects.Rectangle;
    private backgroundCover!: Phaser.GameObjects.Rectangle;
    private retryLabel?: Phaser.GameObjects.Text;
    private overlayKey?: string;
    private failures: string[] = [];
    private realProgress = 0;
    private visualProgress = 0;
    private logoAnimationCompleted = false;
    private assetsLoaded = false;
    private sceneReady = false;
    private transitionStarted = false;
    private loadId = 0;

    constructor() { super("PreloadScene"); }

    public init(): void {
        GameManager.setCurrentScene(this);
        this.loadId++;
        this.failures = [];
        this.realProgress = 0;
        this.visualProgress = 0;
        this.logoAnimationCompleted = false;
        this.assetsLoaded = false;
        this.sceneReady = false;
        this.transitionStarted = false;
        this.retryLabel = undefined;
    }

    public preload(): void {
        this.composeScene();
        this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, this.onLoadError, this);
        this.load.on(Phaser.Loader.Events.PROGRESS, this.updateLoadedPercentage, this);
        this.load.image("bg_1", "assets/bg_01.jpg");
        this.load.image("bg_1_portrait", "assets/bg_01_portrait.jpg");
        this.load.image("splash_landscape", "assets/splash-background-landscape.jpg");
        this.load.image("splash_portrait", "assets/splash-background-portrait.jpg");
        this.load.atlas("texture_atlas_1", "assets/texture_atlas_1.png", "assets/texture_atlas_1.json");
        this.load.audioSprite("audiosprite", "assets/audio/audiosprite.json", ["assets/audio/audiosprite.mp3"]);
    }

    public async create(): Promise<void> {
        this.sceneReady = true;
        const loadId = this.loadId;
        if (this.failures.length > 0) {
            this.showRetry(`Could not load: ${this.failures.join(", ")}\nClick to retry.`);
            return;
        }
        try {
            await Promise.all([
                document.fonts.load('28px "NunitoSans"'),
                document.fonts.load('28px "Baloo2-Bold"'),
                document.fonts.load('28px "Baloo2-SemiBold"')
            ]);
        } catch (error) {
            if (loadId !== this.loadId) return;
            console.error("Could not load Euchre fonts", error);
            this.showRetry("Could not load fonts.\nClick to retry.");
            return;
        }
        if (loadId !== this.loadId || !this.sys.isActive()) return;
        this.assetsLoaded = true;
        this.realProgress = 1;
        this.tryStartTransition();
    }

    private composeScene(): void {
        this.background = new Phaser.GameObjects.Rectangle(this, 0, 0, this.scale.width, this.scale.height, 0x2A0B20)
            .setOrigin(0);
        this.add.existing(this.background);
        this.backgroundCover = new Phaser.GameObjects.Rectangle(this, 0, 0, this.scale.width, this.scale.height, 0x2A0B20)
            .setOrigin(0);
        this.add.existing(this.backgroundCover);
        this.scaledItemsContainer = this.add.container(0, 0);
        this.relayout();
        this.addLogo();
        this.addProgressBar();
        this.events.on(Phaser.Scenes.Events.UPDATE, this.updateVisualProgress, this);
        this.scale.on(Phaser.Scale.Events.RESIZE, this.relayout, this);
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    }

    public relayout(): void {
        this.background.setSize(this.scale.width, this.scale.height);
        this.backgroundCover.setSize(this.scale.width, this.scale.height);
        fitLayout(this, this.scaledItemsContainer);
        this.scaledItemsContainer.setPosition(this.scale.width / 2, this.scale.height / 2);
    }

    private addProgressBar(): void {
        const barWidth = 350;
        const barHeight = 10;
        const borderWidth = 3;
        const progressContainer = new Phaser.GameObjects.Container(this, 0, 35);
        this.scaledItemsContainer.add(progressContainer);

        const shadow = new Phaser.GameObjects.Graphics(this);
        shadow.fillStyle(0x000000, .25);
        shadow.fillRect(-barWidth / 2 - borderWidth + 2, -borderWidth + 2, barWidth + borderWidth * 2, barHeight + borderWidth * 2);
        progressContainer.add(shadow);

        const border = new Phaser.GameObjects.Graphics(this);
        border.fillStyle(0x6e4712, 1);
        border.fillRect(-barWidth / 2 - borderWidth, -borderWidth, barWidth + borderWidth * 2, barHeight + borderWidth * 2);
        progressContainer.add(border);

        const barBackground = new Phaser.GameObjects.Graphics(this);
        barBackground.fillStyle(0x4a2e05, 1);
        barBackground.fillRect(-barWidth / 2, 0, barWidth, barHeight);
        progressContainer.add(barBackground);

        this.progressBar = new Phaser.GameObjects.Graphics(this);
        this.progressBar.fillGradientStyle(0xfffde6, 0xfffde6, 0xcd9a34, 0x76470d, 1);
        this.progressBar.fillRect(0, 0, barWidth, barHeight);
        this.progressBar.x = -barWidth / 2;
        this.progressBar.scaleX = 0;
        progressContainer.add(this.progressBar);
    }

    private addLogo(): void {
        const logo = new Phaser.GameObjects.Image(this, 0, -155, "catafractum-logo");
        this.scaledItemsContainer.add(logo);
        const width = logo.width;
        const height = logo.height;
        const overlayKey = this.overlayKey = `logo-overlay-${Phaser.Utils.String.UUID()}`;
        const overlayTexture = this.textures.createCanvas(overlayKey, width, 2 * height);
        if (!overlayTexture) throw new Error("Could not create the Catafractum logo reveal texture");
        const context = overlayTexture.getContext();
        const rgb = Phaser.Display.Color.IntegerToRGB(0x2A0B20);
        const gradient = context.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`);
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
        context.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
        context.fillRect(0, height, width, height);
        overlayTexture.refresh();

        const overlay = new Phaser.GameObjects.Image(this, 0, -155 - height / 2, overlayKey);
        this.scaledItemsContainer.add(overlay);
        this.tweens.add({
            targets: overlay,
            y: overlay.y + 2 * height,
            duration: 2000,
            ease: Phaser.Math.Easing.Cubic.Out,
            onComplete: () => {
                overlay.destroy();
                this.removeOverlayTexture();
                this.logoAnimationCompleted = true;
                this.tryStartTransition();
            }
        });
    }

    private updateLoadedPercentage(percentageLoaded: number): void {
        this.realProgress = Math.max(this.realProgress, percentageLoaded);
    }

    private updateVisualProgress(): void {
        const target = Phaser.Math.SmoothStep(Phaser.Math.Clamp(this.realProgress, 0, 1), 0, 1);
        this.visualProgress += (target - this.visualProgress) * .18;
        if (Math.abs(target - this.visualProgress) < .0015) this.visualProgress = target;
        this.progressBar.scaleX = Phaser.Math.Clamp(this.visualProgress, 0, 1);
    }

    private tryStartTransition(): void {
        if (this.transitionStarted || !this.sceneReady || !this.assetsLoaded || !this.logoAnimationCompleted) return;
        this.transitionStarted = true;
        this.tweens.add({
            targets: [this.scaledItemsContainer, this.backgroundCover],
            alpha: 0,
            duration: 450,
            ease: Phaser.Math.Easing.Cubic.Out,
            onComplete: () => GameManager.onAssetsLoaded()
        });
    }

    private onLoadError(file: Phaser.Loader.File): void {
        console.error("Could not load Euchre asset", file.key, file.url);
        this.failures.push(file.key);
    }

    private showRetry(message: string): void {
        this.retryLabel?.destroy();
        this.retryLabel = new Phaser.GameObjects.Text(this, 0, 90, message, {
            fontFamily: "Baloo2-Bold", fontSize: "28px", color: GameConstants.TEXT_COLOR, align: "center"
        }).setOrigin(.5).setWordWrapWidth(800).setInteractive({ useHandCursor: true });
        this.scaledItemsContainer.add(this.retryLabel);
        this.retryLabel.once(Phaser.Input.Events.POINTER_UP, () => this.scene.restart());
    }

    private removeOverlayTexture(): void {
        if (this.overlayKey) this.textures.remove(this.overlayKey);
        this.overlayKey = undefined;
    }

    private onShutdown(): void {
        this.loadId++;
        this.events.off(Phaser.Scenes.Events.UPDATE, this.updateVisualProgress, this);
        this.scale.off(Phaser.Scale.Events.RESIZE, this.relayout, this);
        this.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, this.onLoadError, this);
        this.load.off(Phaser.Loader.Events.PROGRESS, this.updateLoadedPercentage, this);
        this.tweens.killAll();
        this.removeOverlayTexture();
    }
}
